import { useState, useContext } from 'react'
import { ModalContext } from '../ModalContext'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import { WhichWeightAPI } from 'WhichWeightAPI'

export default function ComplianceConfigurationModal() {

    const { modalData, HideModal } = useContext(ModalContext)

    const [complianceMobilityWeeklyRequirement, setComplianceMobilityWeeklyRequirement] = useState(modalData.complianceMobilityWeeklyRequirement ?? '')
    const [complianceStrengthWeeklyRequirement, setComplianceStrengthWeeklyRequirement] = useState(modalData.complianceStrengthWeeklyRequirement ?? '')
    const [complianceWeekSpan, setComplianceWeekSpan] = useState(modalData.complianceWeekSpan ?? '')
    const [errorText, setErrorText] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const OnSave = async () => {

        const parsedComplianceMobilityWeeklyRequirement = parseInt(complianceMobilityWeeklyRequirement)
        const parsedComplianceStrengthWeeklyRequirement = parseInt(complianceStrengthWeeklyRequirement)
        const parsedComplianceWeekSpan = parseInt(complianceWeekSpan)

        if (isNaN(parsedComplianceMobilityWeeklyRequirement) || parsedComplianceMobilityWeeklyRequirement < 0) {
            setErrorText('Please enter a valid mobility compliance requirement')
            return
        }

        if (isNaN(parsedComplianceStrengthWeeklyRequirement) || parsedComplianceStrengthWeeklyRequirement < 0) {
            setErrorText('Please enter a valid strength compliance requirement')
            return
        }

        if (isNaN(parsedComplianceWeekSpan) || parsedComplianceWeekSpan <= 0) {
            setErrorText('Please enter a valid duration in weeks')
            return
        }

        setErrorText('')
        setIsLoading(true)

        const jsonBody = {
            groupId: modalData.groupId,
            complianceMobilityWeeklyRequirement: parsedComplianceMobilityWeeklyRequirement,
            complianceStrengthWeeklyRequirement: parsedComplianceStrengthWeeklyRequirement,
            complianceWeekSpan: parsedComplianceWeekSpan,
        }

        const result = await WhichWeightAPI('REPORTING', 'POST', '/group_compliance_config', jsonBody)

        if (result.data.status === 'failure') {
            console.error(result)
            setErrorText('Failed to save compliance configuration. Please try again.')
            setIsLoading(false)
            return
        }

        setIsLoading(false)
        HideModal()
        window.location.reload()

    }

    return (
        <div className="space-y-4">
            <div className="flex flex-col items-center gap-y-3">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-orange-400">
                    <Cog6ToothIcon className="h-8 w-8 text-white" aria-hidden="true" />
                </div>

                <h2 className="text-lg font-semibold text-gray-900">Edit Group Compliance</h2>
            </div>

            <div>
                <label htmlFor="complianceMobilityWeeklyRequirement" className="text-left block text-sm font-medium text-gray-900">
                    Mobility Workout Requirement
                </label>

                <p className='text-left text-xs text-gray-500 mb-2'>
                    How many mobility workouts need to be completed in a week to reach 100% compliance?
                </p>

                <input
                    type="number"
                    id="complianceMobilityWeeklyRequirement"
                    min="0"
                    className="block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent"
                    placeholder="e.g. 3"
                    value={complianceMobilityWeeklyRequirement}
                    onChange={(e) => setComplianceMobilityWeeklyRequirement(e.target.value)}
                    autoFocus
                />
            </div>

            <div>
                <label htmlFor="complianceStrengthWeeklyRequirement" className="text-left block text-sm font-medium text-gray-900">
                    Strength Compliance Requirement
                </label>

                <p className='text-left text-xs text-gray-500 mb-2'>
                    How many strength workouts need to be completed in a week to reach 100% compliance?
                </p>

                <input
                    type="number"
                    id="complianceStrengthWeeklyRequirement"
                    min="0"
                    className="block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent"
                    placeholder="e.g. 3"
                    value={complianceStrengthWeeklyRequirement}
                    onChange={(e) => setComplianceStrengthWeeklyRequirement(e.target.value)}
                />
            </div>

            <div>
                <label htmlFor="complianceWeekSpan" className="text-left block text-sm font-medium text-gray-900">
                    Duration Weeks
                </label>

                <p className='text-left text-xs text-gray-500 mb-2'>
                    How many weeks into the past should the compliance data be calculated from (present week is included)?
                </p>

                <input
                    type="number"
                    id="complianceWeekSpan"
                    min="1"
                    className="block w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400 focus:border-transparent"
                    placeholder="e.g. 4"
                    value={complianceWeekSpan}
                    onChange={(e) => setComplianceWeekSpan(e.target.value)}
                />
            </div>

            {errorText !== '' && (
                <div className="rounded-md bg-red-50 p-3 border border-red-200">
                    <p className="text-sm text-red-800">{errorText}</p>
                </div>
            )}

            <BrandColorButton
                text="Save"
                action={OnSave}
                isLoading={isLoading}
            />
        </div>
    )
}
